import React, { Activity, useMemo, type FC } from 'react';
import type { TextStyle, ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { Select } from '@/components/ui/select';
import type { SelectOption } from '@/components/ui/select/types';
import { Text, TextProps } from '@/components/ui/text';
import type { TextInputStatus } from '@/components/ui/text-input/types';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';

export type FormSelectInputProps = {
  /**
   * Optional label displayed above the trigger.
   * If omitted, no label row is rendered.
   */
  label?: string;
  required?: boolean;
  disabled?: boolean;

  placeholder?: string;

  options: SelectOption[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string) => void;

  /**
   * Close dropdown after selecting an item (default: true)
   */
  closeOnSelect?: boolean;
  status?: Exclude<TextInputStatus, 'disabled'>;
  helperText?: string;

  containerStyle?: ViewStyle | ViewStyle[];
  triggerStyle?: ViewStyle | ViewStyle[];
  /**
   * If provided, overrides label style.
   * (Useful when you need slightly different spacing.)
   */
  labelStyle?: TextStyle | TextStyle[];
  labelProps?: Omit<TextProps, 'children' | 'style'>;
  statusBarTranslucent?: boolean;
  errorMessage?: string;
};

export const FormSelectInput: FC<FormSelectInputProps> = ({
  label,
  required = true,
  disabled = false,
  placeholder,
  options,
  value,
  defaultValue,
  onValueChange,
  closeOnSelect = true,
  status = 'default',
  helperText,
  containerStyle,
  triggerStyle,
  labelStyle,
  labelProps,
  statusBarTranslucent = false,
  errorMessage,
}) => {
  const displayLabel = useMemo(() => String(label ?? '').toUpperCase(), [label]);
  const resolvedPlaceholder = placeholder ?? label ?? '';

  const styles = useStyles();

  return (
    <Stack direction="column" gap={8} width="100%" style={containerStyle}>
      {label ? (
        <Stack direction="row" align="center" justify="flex-start">
          <Text
            variant="Font Inter/Subtitle/Regular 11px"
            style={[styles.label, labelStyle]}
            {...labelProps}>
            {displayLabel}
          </Text>
          {required && (
            <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.requiredMark}>
              *
            </Text>
          )}
        </Stack>
      ) : null}

      <Select
        options={options}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        status={status}
        helperText={helperText}
        closeOnSelect={closeOnSelect}>
        <Select.Trigger style={triggerStyle} hideIcon={false} />
        <Select.Content maxHeight={500} statusBarTranslucent={statusBarTranslucent} />
      </Select>

      <Activity mode={errorMessage ? 'visible' : 'hidden'}>
        <Text
          variant="Font Inter/Subtitle/Regular 11px"
          style={[styles.helperText, { color: colors.Error }]}>
          {errorMessage}
        </Text>
      </Activity>
    </Stack>
  );
};

const useStyles = createStyles(() => ({
  helperText: {
    marginTop: -2,
  },
  label: {
    color: colors['Labels/Primary'],
  },
  requiredMark: {
    marginLeft: 4,
    color: colors.Red,
  },
}));

export default FormSelectInput;
